// Carriers list filter row — FE-SCRM-030.
//
// Search input (debounced 300ms per spec), carrier_type dropdown,
// and status segmented control. URL state lives upstream in
// ``CarriersListPage``; this component is controlled.

import { Search } from "lucide-react";
import { useEffect, useState } from "react";

import {
  CARRIER_STATUSES,
  CARRIER_TYPES,
  type CarrierListFilters,
  type CarrierStatus,
  type CarrierType,
} from "./types";
import { CARRIER_TYPE_LABEL } from "./labels";


interface Props {
  filters: CarrierListFilters;
  onChange: (next: CarrierListFilters) => void;
}


export function CarriersListFilters({ filters, onChange }: Props) {
  const [searchInput, setSearchInput] = useState(filters.q ?? "");

  useEffect(() => {
    setSearchInput(filters.q ?? "");
  }, [filters.q]);

  // 300ms debounce per the spec. Pressing Enter inside the search
  // form still applies immediately via ``onSubmit`` (form-level).
  useEffect(() => {
    const trimmed = searchInput.trim();
    const current = filters.q ?? "";
    if (trimmed === current) return;
    const t = window.setTimeout(() => {
      onChange({ ...filters, q: trimmed || undefined });
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, filters.q]);

  return (
    <div
      data-testid="carriers-list-filters"
      className="flex flex-col gap-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            onChange({
              ...filters, q: searchInput.trim() || undefined,
            });
          }}
          className="flex-1 sm:max-w-xs"
        >
          <label className="relative flex items-center">
            <span className="sr-only">Search carriers</span>
            <Search
              size={14}
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 text-text-tertiary"
            />
            <input
              type="search"
              data-testid="carriers-list-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search carriers"
              className="w-full rounded-md border border-border-subtle bg-raised py-1.5 pl-7 pr-3 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
            />
          </label>
        </form>

        <CarrierTypeSelect
          value={filters.carrier_type}
          onChange={(next) =>
            onChange({ ...filters, carrier_type: next })}
        />

        <StatusToggle
          value={filters.status ?? "active"}
          onChange={(next) => onChange({ ...filters, status: next })}
        />
      </div>
    </div>
  );
}


function CarrierTypeSelect({
  value, onChange,
}: {
  value: CarrierType | undefined;
  onChange: (next: CarrierType | undefined) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-text-secondary">
      <span className="sr-only">Filter by carrier type</span>
      <select
        data-testid="carriers-list-type-select"
        value={value ?? ""}
        onChange={(e) =>
          onChange(
            e.target.value
              ? (e.target.value as CarrierType)
              : undefined,
          )
        }
        className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-xs text-text-primary outline-none focus:border-teal focus:shadow-focus"
      >
        <option value="">All types</option>
        {CARRIER_TYPES.map((t) => (
          <option key={t} value={t}>
            {CARRIER_TYPE_LABEL[t]}
          </option>
        ))}
      </select>
    </label>
  );
}


function StatusToggle({
  value, onChange,
}: {
  value: CarrierStatus | "all";
  onChange: (next: CarrierStatus | "all") => void;
}) {
  const opts: Array<{ value: CarrierStatus | "all"; label: string }> = [
    { value: "active", label: "Active" },
    ...CARRIER_STATUSES
      .filter((s) => s !== "active")
      .map((s) => ({
        value: s,
        label: s.charAt(0).toUpperCase() + s.slice(1),
      })),
    { value: "all", label: "All" },
  ];
  return (
    <div
      data-testid="carriers-list-status-toggle"
      role="group"
      aria-label="Carrier status"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      {opts.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            data-testid={`carriers-list-status-${opt.value}`}
            className={[
              "rounded px-2.5 py-1 text-xs font-medium transition-fast ease-out",
              active
                ? "bg-teal-tint text-text-primary"
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
